import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Loader2, Wand2, FileText, CheckCircle2, AlertTriangle } from 'lucide-react';
import { toast } from '../hooks/use-toast';

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('lexduo_token')}` } });

/**
 * SelectorPlantillaDialog (v83) — Asistente guiado de generación de reclamación.
 * Pre-selecciona conceptos según el análisis IA, sugiere plantilla y deja
 * elegir entre candidatos (override) antes de generar.
 */
export default function SelectorPlantillaDialog({ isOpen, onClose, reclamacionId, onSuccess }) {
  const [cat, setCat] = useState(null);
  const [conceptos, setConceptos] = useState([]);
  const [argumentoCa, setArgumentoCa] = useState('');
  const [variante, setVariante] = useState('estandar');
  const [candidatos, setCandidatos] = useState([]);
  const [sugerida, setSugerida] = useState(null);
  const [selected, setSelected] = useState('');
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);

  const recomputar = useCallback(async (concs, arg, vari) => {
    try {
      const res = await axios.post(
        `${API}/api/extrajudicial/reclamaciones/${reclamacionId}/plantillas/sugerir`,
        { conceptos: concs, argumento_ca: arg || null, variante: vari || 'estandar' }, auth()
      );
      setCandidatos(res.data.candidatos || []);
      setSugerida(res.data.sugerida || null);
      setSelected((res.data.sugerida && res.data.sugerida.codigo) || '');
    } catch (e) { /* noop */ }
  }, [reclamacionId]);

  useEffect(() => {
    if (!isOpen) return;
    let cancel = false;
    (async () => {
      setLoading(true);
      try {
        const [c, pre] = await Promise.all([
          axios.get(`${API}/api/extrajudicial/plantillas-catalogo`, auth()),
          axios.get(`${API}/api/extrajudicial/reclamaciones/${reclamacionId}/plantillas/preseleccion`, auth()),
        ]);
        if (cancel) return;
        setCat(c.data);
        const concs = pre.data.conceptos || [];
        setConceptos(concs);
        setArgumentoCa('');
        setVariante('estandar');
        setCandidatos(pre.data.candidatos || []);
        setSugerida(pre.data.sugerida || null);
        setSelected((pre.data.sugerida && pre.data.sugerida.codigo) || '');
      } catch (e) {
        toast({ title: 'Error', description: 'No se pudo cargar el catálogo de plantillas', variant: 'destructive' });
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [isOpen, reclamacionId]);

  const toggleConcepto = (c) => {
    const next = conceptos.includes(c) ? conceptos.filter((x) => x !== c) : [...conceptos, c];
    setConceptos(next);
    recomputar(next, argumentoCa, variante);
  };
  const onArg = (v) => { setArgumentoCa(v); recomputar(conceptos, v, variante); };
  const onVar = (v) => { setVariante(v); recomputar(conceptos, argumentoCa, v); };

  const handleGenerar = async () => {
    if (!selected) return;
    setGenerating(true);
    try {
      const res = await axios.post(
        `${API}/api/extrajudicial/reclamaciones/${reclamacionId}/generar-reclamacion-guiada`,
        { codigo: selected }, auth()
      );
      toast({ title: '✅ Documento generado', description: res.data.message });
      onClose();
      onSuccess && onSuccess();
    } catch (error) {
      toast({ title: 'Error al generar', description: error.response?.data?.detail || 'No se pudo generar el documento', variant: 'destructive' });
    } finally {
      setGenerating(false);
    }
  };

  const labelConcepto = (c) => cat?.labels?.conceptos?.[c] || c;
  const showArg = conceptos.includes('comision_apertura');

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="selector-plantilla-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wand2 className="w-5 h-5 text-indigo-600" />
            Asistente de Reclamación — selección de plantilla
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-16 text-slate-500 gap-3">
            <Loader2 className="w-5 h-5 animate-spin" /> Cargando catálogo…
          </div>
        ) : (
          <div className="space-y-5">
            <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3 text-xs text-indigo-800">
              Conceptos pre-seleccionados según el análisis IA de la escritura. Revísalos y ajústalos; el asistente sugiere la plantilla y puedes elegir otra si procede.
            </div>

            <div>
              <Label className="mb-2 block">Conceptos / cláusulas reclamadas</Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {(cat?.conceptos_vocab || []).map((c) => (
                  <label key={c} className="flex items-center gap-2 px-3 py-2 rounded border hover:bg-slate-50 cursor-pointer" data-testid={`concepto-${c}`}>
                    <Checkbox checked={conceptos.includes(c)} onCheckedChange={() => toggleConcepto(c)} />
                    <span className="text-sm">{labelConcepto(c)}</span>
                  </label>
                ))}
              </div>
            </div>

            {showArg && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label>Argumento Comisión de Apertura</Label>
                  <Select value={argumentoCa} onValueChange={onArg}>
                    <SelectTrigger data-testid="argumento-ca-select"><SelectValue placeholder="Seleccionar argumento…" /></SelectTrigger>
                    <SelectContent>
                      {(cat?.argumentos_ca || []).map((a) => (
                        <SelectItem key={a} value={a}>{cat?.labels?.argumentos_ca?.[a] || a}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Variante</Label>
                  <Select value={variante} onValueChange={onVar}>
                    <SelectTrigger data-testid="variante-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {(cat?.variantes || []).map((v) => (
                        <SelectItem key={v} value={v}>{cat?.labels?.variantes?.[v] || v}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            <div>
              <Label className="mb-2 block">Plantilla a generar ({candidatos.length} candidatas)</Label>
              {sugerida && (
                <div className="flex items-center gap-2 text-xs text-emerald-700 mb-2">
                  <CheckCircle2 className="w-4 h-4" /> Sugerida: <strong>{sugerida.nombre}</strong>
                </div>
              )}
              <div className="space-y-1 max-h-64 overflow-y-auto" data-testid="candidatos-list">
                {candidatos.map((p) => (
                  <label
                    key={p.codigo}
                    className={`flex items-start gap-3 px-3 py-2 rounded border cursor-pointer ${selected === p.codigo ? 'border-indigo-500 bg-indigo-50' : 'hover:bg-slate-50'}`}
                    data-testid={`candidato-${p.codigo}`}
                  >
                    <input type="radio" name="plantilla" className="mt-1" checked={selected === p.codigo} onChange={() => setSelected(p.codigo)} />
                    <FileText className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
                    <div className="flex-1">
                      <div className="text-sm font-medium">{p.nombre}</div>
                      <div className="text-[11px] text-slate-500">{p.categoria} · afinidad {p.score}</div>
                    </div>
                  </label>
                ))}
                {candidatos.length === 0 && (
                  <div className="flex items-center gap-2 text-amber-700 text-sm py-3">
                    <AlertTriangle className="w-4 h-4" /> No hay plantillas. Selecciona al menos un concepto.
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={generating} data-testid="selector-cancel-btn">Cancelar</Button>
          <Button onClick={handleGenerar} disabled={generating || !selected} data-testid="selector-generar-btn">
            {generating && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
            {generating ? 'Generando…' : 'Generar reclamación'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
